import React, { useState, useCallback, useMemo, useContext } from 'react';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { useDropzone } from 'react-dropzone';
import Paper from '@material-ui/core/Paper';
import { toBase64 } from '../../utils';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import Select from '@material-ui/core/Select';
import AddIcon from '@material-ui/icons/Add';
import InputAdornment from '@material-ui/core/InputAdornment';
import { useHistory } from 'react-router-dom';
import AuthContext from '../../authContext';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import axiosInstance from '../../axiosInstance';
import DoneIcon from '@material-ui/icons/Done';

const baseStyle = {
  display: 'flex',
  height: 160,
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: 'rgb(238, 238, 238)',
  borderStyle: 'dashed',
  backgroundColor: 'rgb(250, 250, 250)',
  color: 'rgb(189, 189, 189)',
  outline: 'none',
  transition: 'border .24s ease-in-out'
};
const activeStyle = {
  borderColor: 'rgb(33, 150, 243)'
};
const acceptStyle = {
  borderColor: 'rgb(0, 230, 118)'
};
const rejectStyle = {
  borderColor: 'rgb(255, 23, 68)'
};
const thumbsContainer = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center'
};
const thumb = {
  display: 'inline-flex',
  borderRadius: 2,
  height: 204,
  boxSizing: 'border-box'
};
const thumbInner = {
  display: 'flex',
  minWidth: 0,
  overflow: 'hidden'
};
const img = {
  display: 'block',
  width: 'auto',
  height: '100%',
  objectFit: 'contain'
};

const useStyles = makeStyles({
  create: {
    width: '100%',
    height: 'calc(100% - 112px)',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: '#FDFDFE'
  },
  paper: {
    width: '800px',
    display: 'flex',
    flexDirection: 'column',
    padding: '30px',
    margin: '30px'
  },
  row: {
    display: 'flex',
    justifyContent: 'center'
  },
  title: {
    marginBottom: '10px'
  },
  centerRow: {
    display: 'flex',
    justifyContent: 'center'
  },
  goalButton: {
    width: '180px',
    margin: '10px'
  },
  goal: {
    display: 'flex',
    justifyContent: 'center',
    margin: '10px'
  },
  name: {
    margin: '10px'
  },
  address: {
    margin: '10px'
  },
  category: {
    margin: '10px'
  },
  file: {
    margin: '10px'
  },
  description: {
    margin: '10px'
  },
  createButton: {
    margin: '10px'
  },
  goalName: {
    marginRight: '20px',
    width: '250px'
  },
  goalTarget: {
    marginLeft: '20px',
    width: '80px'
  },
  backdrop: {
    zIndex: 99999,
    // Prevent the user from selecting the text.
    userSelect: 'none',
    msUserSelect: 'none',
    msTouchSelect: 'none',
    WebkitUserSelect: 'none',
    KhtmlUserSelect: 'none',
    MozUserSelect: 'none',
    cursor: 'default'
  },
  greenButton: {
    backgroundColor: '#47C072',
    '&:hover': {
      backgroundColor: '#10A674'
    }
  }
});

const Create = () => {
  const classes = useStyles();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [fileBase64, setFileBase64] = useState('');
  const [file, setFile] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [goals, setGoals] = useState([]);
  const [snackbarSuccessOpen, setSnackbarSuccessOpen] = useState(false);
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [addingGoal, setAddingGoal] = useState(false);
  const history = useHistory();
  const { currentUser } = useContext(AuthContext);

  const onCategoryChanged = (event) => {
    setCategory(event.target.value);
  };

  const onAddressChanged = (event) => {
    setAddress(event.target.value);
  };

  const onDescriptionChanged = (event) => {
    setDescription(event.target.value);
  };

  const onNameChanged = (event) => {
    setName(event.target.value);
  };

  const onGoalChanged = (event, index) => {
    const newGoal = {
      goal: event.target.value,
      goalTarget: goals[index].goalTarget
    };

    setGoals((previousGoals) => {
      const newGoals = [...previousGoals];
      newGoals[index] = newGoal;

      return newGoals;
    });
  };

  const onGoalTargetChanged = (event, index) => {
    const newGoal = {
      goal: goals[index].goal,
      goalTarget: +event.target.value
    };

    setGoals((previousGoals) => {
      const newGoals = [...previousGoals];
      newGoals[index] = newGoal;

      return newGoals;
    });
  };

  const onAddGoalButtonClicked = () => {
    setGoals((previousGoals) => {
      return [...previousGoals, {
        goal: '',
        goalTarget: 1000
      }];
    });

    setAddingGoal(true);
  };

  const onSubmitGoalButtonClicked = () => {
    setAddingGoal(false);
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    const fileBase64 = await toBase64(file);
    setFileBase64(fileBase64);
    setFile(file);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({ accept: 'image/*', onDrop });

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {})
  }), [
    isDragActive,
    isDragAccept,
    isDragReject
  ]);

  const generateGoalElement = (index) => {
    return (
      <div className={classes.goal} key={index}>
        <TextField
          required
          value={goals[index].goal}
          onChange={(event) => onGoalChanged(event, index)}
          label="Goal"
          className={classes.goalName}
        />
        <FormControl className={classes.goalTarget}>
          <InputLabel>Target *</InputLabel>
          <Input
            value={goals[index].goalTarget}
            onChange={(event) => onGoalTargetChanged(event, index)}
            startAdornment={<InputAdornment position="start">€</InputAdornment>}
          />
        </FormControl>
      </div>
    );
  };

  const onCreateButtonClicked = () => {
    const project = {
      name,
      address,
      creator: {
        name: currentUser.displayName,
        email: currentUser.email,
        photoURL: currentUser.photoURL
      },
      category,
      image: fileBase64,
      description,
      goals,
      creationDate: +new Date(),
    };

    setBackdropOpen(true);

    currentUser.getIdToken(true)
      .then((idToken) => {
        return axiosInstance.post(`/${currentUser.uid}.json?auth=${idToken}`, project);
      })
      .then((resp) => {
        setTimeout(() => {
          history.push(`/project/${resp.data.name}`);
        }, 2000);

        setBackdropOpen(false);

        setSnackbarSuccessOpen(true);
      });
  };

  return (
    <div className={classes.create}>
      <Paper className={classes.paper} elevation={3}>
        <div className={classes.row}>
          <div className={classes.title}>
            <Typography variant="h2" color="textSecondary" component="p">
              Create Project
          </Typography>
          </div>
        </div>
        <TextField
          required
          value={name}
          onChange={onNameChanged}
          label="Name"
          className={classes.name}
        />
        <TextField
          required
          value={address}
          onChange={onAddressChanged}
          label="Address"
          className={classes.address}
        />
        <FormControl className={classes.category}>
          <InputLabel>Category *</InputLabel>
          <Select
            required
            value={category}
            onChange={onCategoryChanged}
          >
            <MenuItem value="Hotel">Hotel</MenuItem>
            <MenuItem value="Restaurant">Restaurant</MenuItem>
            <MenuItem value="Grocery">Grocery</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>
        <div className={classes.file}>
          {
            !file &&
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              <p>Drag & Drop a Image</p>
            </div>
          }
          {
            file &&
            <aside style={thumbsContainer}>
              <div style={thumb}>
                <div style={thumbInner}>
                  <img
                    src={URL.createObjectURL(file)}
                    style={img}
                    alt={file.name}
                  />
                </div>
              </div>
            </aside>
          }
        </div>
        <TextField
          label="Description"
          multiline
          rows={5}
          placeholder="My business..."
          value={description}
          onChange={onDescriptionChanged}
          className={classes.description}
        />
        <div className={classes.centerRow}>
          <Typography variant="h4">
            Goals
          </Typography>
        </div>
        {
          goals.map((_, index) => generateGoalElement(index))
        }
        {!addingGoal ?
          <div className={classes.centerRow}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              className={classes.goalButton}
              onClick={onAddGoalButtonClicked}
            >
              Add Goal
          </Button>
          </div> :
          <div className={classes.centerRow}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DoneIcon />}
              className={`${classes.goalButton} ${classes.greenButton}`}
              onClick={onSubmitGoalButtonClicked}
            >
              Submit Goal
            </Button>
          </div>
        }
        <Button
          variant="contained"
          color="primary"
          onClick={onCreateButtonClicked}
          className={`${classes.createButton} ${classes.greenButton}`}
        >
          Create
        </Button>
      </Paper>
      <Backdrop
        className={classes.backdrop}
        open={backdropOpen}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <Snackbar
        open={snackbarSuccessOpen}
        autoHideDuration={1500}
        onClose={() => setSnackbarSuccessOpen(false)}
      >
        <MuiAlert elevation={6} variant="filled" severity="success">
          Successfully saved Project! Redirecting...
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default Create;
